import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Save, ListOrdered, Users } from 'lucide-react';

const inputCls = 'mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/35';
const empty = { name: '', title: '', bio: '', photo_url: '', instagram_url: '', facebook_url: '', whatsapp_url: '', active: true, order: 0 };

export default function TeamTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = async () => { const { data } = await api.get('/team?only_active=false'); setItems(data || []); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing }; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.patch(`/team/${editing.id}`, payload);
      else await api.post('/team', payload);
      toast.success('Saved'); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const del = async (it) => { if (!window.confirm(`Delete ${it.name}?`)) return; await api.delete(`/team/${it.id}`); toast.success('Deleted'); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-display text-xl font-semibold">Team members</div>
          <div className="text-xs text-white/55">Profile cards shown on the Our Team page</div>
        </div>
        <Button data-testid="admin-add-team-button" onClick={() => { setEditing(empty); setOpen(true); }} className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] rounded-xl"><Plus className="mr-1 h-4 w-4" /> Add member</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <div className="col-span-full rounded-3xl border border-white/10 bg-white/5 p-10 text-center text-white/55"><Users className="mx-auto h-8 w-8 mb-2" />No team members yet.</div>}
        {items.map((it) => (
          <Card key={it.id} className="rounded-2xl border-white/10 bg-white/5 overflow-hidden">
            <div className="aspect-[4/3] bg-[#0d223d] relative">{it.photo_url ? <img src={it.photo_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}{!it.active && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#0a1628]/85 border border-white/15 text-white/75">Hidden</span>}</div>
            <div className="p-4">
              <div className="font-display font-semibold">{it.name}</div>
              <div className="text-xs text-[#00b4d8] mt-0.5">{it.title}</div>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => { setEditing(it); setOpen(true); }} className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(it)} className="text-red-400 hover:text-red-200 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                <div className="ml-auto text-[11px] text-white/45 inline-flex items-center gap-1"><ListOrdered className="h-3 w-3" /> {it.order}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl bg-[#07101f] border-white/12 text-white">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? 'Edit member' : 'New member'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin pr-1">
              <ImageUploader label="Profile photo" value={editing.photo_url} onChange={(v) => setEditing({ ...editing, photo_url: v })} />
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name</Label><Input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div><Label>Title / Role</Label><Input className={inputCls} placeholder="Scuba Diving Instructor" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              </div>
              <div>
                <Label>Bio <span className="text-white/45 text-xs">(up to 500 chars — card shows a Read more toggle for long bios)</span></Label>
                <Textarea rows={8} maxLength={500} className={inputCls} value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} />
                <div className={`text-[11px] mt-1 text-right ${((editing.bio || '').length) >= 500 ? 'text-red-300' : 'text-white/40'}`}>
                  {(editing.bio || '').length} / 500 chars
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>Instagram</Label><Input className={inputCls} value={editing.instagram_url} onChange={(e) => setEditing({ ...editing, instagram_url: e.target.value })} /></div>
                <div><Label>Facebook</Label><Input className={inputCls} value={editing.facebook_url} onChange={(e) => setEditing({ ...editing, facebook_url: e.target.value })} /></div>
                <div><Label>WhatsApp</Label><Input className={inputCls} value={editing.whatsapp_url} onChange={(e) => setEditing({ ...editing, whatsapp_url: e.target.value })} /></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label>Visible on site</Label></div>
                <div className="flex items-center gap-2"><Label>Order</Label><Input type="number" className={`w-20 ${inputCls}`} value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || '0') })} /></div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/65 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={save} disabled={saving || !editing?.name} className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100]">{saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : <><Save className="mr-2 h-4 w-4" /> Save</>}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
